import {forwardRef} from 'react';
import {useIntl} from 'react-intl';
import {Button, ButtonProps, Center, Flex} from '@chakra-ui/react';
import {Reorder} from 'framer-motion';

import {useTranslate} from '@eksab/i18n';

import {AnswerHeading, type AnswersProps} from './';
import {useTriviaStore} from '../../state';
import type {Answer, TextAnswer} from '../../types';

export const OrderableAnswers = ({submitAnswerMutation, ...props}: AnswersProps) => {
  const t = useTranslate();
  const answers = useTriviaStore((state) => {
    if (state.currentQuestion!.answers_type !== 'ordering') {
      throw new Error('`OrderableAnswers` component does not support answer types of `text` or `image`.');
    }

    const orderedAnswers = state.currentAnswer as TextAnswer[];
    const unorderedAnswers = state.currentQuestion!.answers.filter(
      (answer) => !orderedAnswers.find((orderedAnswer) => orderedAnswer.id === answer.id),
    );

    return [...orderedAnswers, ...unorderedAnswers];
  });

  const onAnswerChange = (answer: Answer) => {
    if (submitAnswerMutation.isLoading) return;

    useTriviaStore.setState((state) => {
      const answerIndex = state.currentAnswer.findIndex(({id}) => id === answer.id);
      const isAnswerSelected = answerIndex !== -1;

      if (isAnswerSelected) {
        const answersUptoUnselectedOne = state.currentAnswer.slice(0, answerIndex);

        return {
          currentAnswer: answersUptoUnselectedOne,
        };
      }

      const reachedMaxAnswers = state.currentAnswer.length === state.currentQuestion!.correct_answers_count;

      if (reachedMaxAnswers) {
        return state;
      }

      const shouldAllAnswersBeOrdered = answers.length === state.currentQuestion!.correct_answers_count;
      const isOneAnswerLeft = state.currentAnswer.length === answers.length - 2;

      if (shouldAllAnswersBeOrdered && isOneAnswerLeft) {
        const remainingAnswer =
          answers[answers.length - 1].id === answer.id ? answers[answers.length - 2] : answers[answers.length - 1];

        return {
          currentAnswer: [...state.currentAnswer, answer, remainingAnswer],
        };
      }

      return {
        currentAnswer: [...state.currentAnswer, answer],
      };
    });
  };

  return (
    <Flex flexDir="column" gap="8" {...props}>
      <AnswerHeading>{t('trivia.quiz.answers.ordering.heading')}</AnswerHeading>

      <Flex as={OrderableGroup} values={answers} flexDir="column" gap={{base: 3, lg: 4}}>
        {answers.map((answer) => (
          <OrderableAnswer
            key={answer.id}
            answer={answer}
            isDisabled={submitAnswerMutation.isLoading}
            onClick={() => onAnswerChange(answer)}
          />
        ))}
      </Flex>
    </Flex>
  );
};

interface OrderableAnswersProps extends ButtonProps {
  answer: TextAnswer;
}

const OrderableAnswer = ({answer, ...props}: OrderableAnswersProps) => {
  const format = useIntl().formatNumber;

  const order = useTriviaStore((state) => state.currentAnswer.findIndex(({id}) => id === answer.id));

  const isActive = order !== -1;

  return (
    <Button
      as={OrderableItem}
      value={answer.id}
      id={answer.body}
      data-testid="answer"
      dragListener={false}
      pos="relative"
      variant="unstyled"
      display="flex"
      minH="10"
      h="unset"
      paddingInline="10"
      py="2"
      borderWidth="1px"
      borderColor="#c7c7c7"
      rounded="full"
      bg="#f8f8f8"
      fontSize="md"
      fontWeight="600"
      transitionProperty="border-color, background, color"
      transitionDuration="0.2s"
      _hover={{borderColor: 'gray.500'}}
      _active={{fontWeight: 700, borderColor: 'black', bg: 'black', color: 'white'}}
      {...props}
      isActive={isActive}
      isDisabled={props.isDisabled && !isActive}
    >
      <Center
        as="span"
        pos="absolute"
        insetInlineStart="0.5"
        boxSize="9"
        rounded="full"
        p="0.5"
        borderWidth="1px"
        borderColor={isActive ? 'green.400' : 'gray.400'}
        bg={isActive ? 'green.400' : 'white'}
        color={isActive ? 'white' : '#c7c7c7'}
        fontSize="md"
        fontWeight="700"
        transitionProperty="background, color, border-color"
        transitionDuration="0.2s"
      >
        {isActive ? format(order + 1) : '-'}
      </Center>

      {answer.body}
    </Button>
  );
};

type OrderableGroupProps = Omit<Parameters<typeof Reorder.Group>[0], 'onReorder'>;

const OrderableGroup = (props: OrderableGroupProps) => <Reorder.Group as="div" {...props} onReorder={() => {}} />;

type OrderableItemProps = Parameters<typeof Reorder.Item>[0];

const OrderableItem = forwardRef(function OrderableItem(props: OrderableItemProps, ref: React.Ref<OrderableItemProps>) {
  return <Reorder.Item as="button" ref={ref} {...props} />;
});
